import dedupeChatItemsByRoomId from "./dedupeChatItemsByRoomId";

const normalizeId = (value) => {
  const rawId = value?._id ?? value?.id ?? value?.roomId ?? value;

  if (rawId === undefined || rawId === null || rawId === "") {
    return null;
  }

  return String(rawId);
};

const getUniqueItemsById = (items = [], fallbackKeyPrefix = "item") => {
  if (!Array.isArray(items)) {
    return [];
  }

  const seenIds = new Set();

  return items.filter((item, index) => {
    const normalizedId = normalizeId(item) ?? `${fallbackKeyPrefix}-${index}`;

    if (seenIds.has(normalizedId)) {
      return false;
    }

    seenIds.add(normalizedId);
    return true;
  });
};

const getMembershipIds = (items = []) => {
  const uniqueItems = getUniqueItemsById(items, "membership");

  return new Set(
    uniqueItems.map((item) => normalizeId(item)).filter(Boolean)
  );
};

const isCurrentUserInParticipants = (participants, currentUserId) => {
  if (!currentUserId) {
    return false;
  }

  return getMembershipIds(participants).has(String(currentUserId));
};

const isCurrentUserExcluded = (item, currentUserId) => {
  if (!currentUserId) {
    return false;
  }

  return getMembershipIds(item?.noParticipants).has(String(currentUserId));
};

const sanitizeGroupDetails = (groupDetails = [], parentItem, currentUserId) => {
  const rawGroups = Array.isArray(groupDetails) ? groupDetails : [];

  return getUniqueItemsById(rawGroups, "group").filter((group) => {
    if (isCurrentUserExcluded(group, currentUserId)) {
      return false;
    }

    if (Array.isArray(group?.participants) && group.participants.length > 0) {
      return isCurrentUserInParticipants(group.participants, currentUserId);
    }

    if (isCurrentUserExcluded(parentItem, currentUserId)) {
      return false;
    }

    return isCurrentUserInParticipants(parentItem?.participants, currentUserId);
  });
};

const sanitizeChatItemForUser = (item, currentUserId) => {
  if (!item || !currentUserId) {
    return null;
  }

  const normalizedParticipants = getUniqueItemsById(
    item?.participants,
    "participant"
  );

  if (!isCurrentUserInParticipants(normalizedParticipants, currentUserId)) {
    return null;
  }

  return {
    ...item,
    participants: normalizedParticipants,
  };
};

const sanitizeChatItemsForUser = (items = [], currentUserId) => {
  if (!Array.isArray(items) || !currentUserId) {
    return [];
  }

  const visibleItems = items
    .map((item) => sanitizeChatItemForUser(item, currentUserId))
    .filter(Boolean);

  return dedupeChatItemsByRoomId(visibleItems);
};

const getChatItemIdentity = (item) =>
  normalizeId(item?.roomId ?? item?._id ?? item?.groupDetails?.[0]?._id);

const isChatItemVisibleToUser = (item, currentUserId) =>
  Boolean(sanitizeChatItemForUser(item, currentUserId));

export default sanitizeChatItemsForUser;
export {
  getChatItemIdentity,
  isChatItemVisibleToUser,
  sanitizeChatItemForUser,
};
